<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import { vOnClickOutside } from '@vueuse/components';
import { formatDistanceToNow, format, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import { useCrmTaskPendenciesStore } from 'dashboard/stores/crmTaskPendencies';
import { ACTIVITY_EMOJI } from 'dashboard/routes/dashboard/kanban/helpers/activityTypes';

// Ícone de pendências de atividade do CRM: contador com novas + atrasadas +
// de hoje do usuário logado, painel com as seções e aviso na hora em que
// alguém atribui uma atividade a ele (evento crm_task.assigned no canal do
// usuário). Além do tempo real, relê a cada 5 minutos: o "hoje" vira
// "atrasada" à meia-noite sem ninguém mexer em nada.
const POLL_MS = 5 * 60 * 1000;

const route = useRoute();
const router = useRouter();
const vuexStore = useStore();
const store = useCrmTaskPendenciesStore();
const { data, isOpen, isLoading } = storeToRefs(store);

const button = ref(null);
const panelStyle = ref({});
let pollTimer = null;

const LABELS = {
  title: 'Suas pendências',
  tooltip: 'Pendências de atividades',
  empty: 'Nenhuma pendência. Tudo em dia.',
  loading: 'Carregando...',
  seeAll: 'Ver agenda completa',
  done: 'Concluir',
  sections: {
    new: 'Novas para você',
    overdue: 'Atrasadas',
    today: 'Hoje',
    upcoming: 'Próximos 7 dias',
  },
};

const sections = computed(() =>
  ['new', 'overdue', 'today', 'upcoming']
    .map(key => ({
      key,
      label: LABELS.sections[key],
      tasks: data.value[key] || [],
    }))
    .filter(section => section.tasks.length)
);

const badge = computed(() => store.badge);

const dueLabel = task => {
  if (!task.due_at) return 'sem data';
  const due = new Date(task.due_at);
  if (isToday(due)) return `hoje às ${format(due, 'HH:mm')}`;
  return formatDistanceToNow(due, { addSuffix: true, locale: ptBR });
};

const contextLabel = task => task.deal_title || task.contact_name || 'Agenda';

// O painel sai ao lado do botão, por cima de tudo: a barra lateral rola e
// encolhe, então a posição é medida na hora de abrir.
const placePanel = () => {
  const rect = button.value?.getBoundingClientRect();
  if (!rect) return;
  panelStyle.value = {
    left: `${Math.round(rect.right + 8)}px`,
    bottom: `${Math.max(8, Math.round(window.innerHeight - rect.bottom))}px`,
  };
};

const toggle = () => {
  if (isOpen.value) {
    store.close();
    return;
  }
  placePanel();
  store.open();
};

const closePanel = () => {
  if (isOpen.value) store.close();
};

const openTask = async task => {
  store.close();
  const accountId = route.params.accountId;
  if (!task.deal_id) {
    router.push({ name: 'kanban_tasks', params: { accountId } });
    return;
  }
  try {
    const deal = await vuexStore.dispatch('crmDeals/fetchDeal', task.deal_id);
    router.push({
      name: 'kanban_funnel',
      params: { accountId, funnelId: String(deal.funnel_id) },
      query: { dealId: String(task.deal_id) },
    });
  } catch {
    router.push({ name: 'kanban_tasks', params: { accountId } });
  }
};

const complete = async task => {
  try {
    await store.complete(task);
  } catch {
    useAlert('Não foi possível concluir a atividade');
  }
};

const onAssigned = task => {
  const emoji = ACTIVITY_EMOJI[task.activity_type] || '📋';
  useAlert(`${emoji} Nova atividade: ${task.title} (${contextLabel(task)})`);
  store.fetch();
};

onMounted(() => {
  store.fetch();
  emitter.on('crm_task_assigned', onAssigned);
  pollTimer = setInterval(() => store.fetch(), POLL_MS);
});

onBeforeUnmount(() => {
  emitter.off('crm_task_assigned', onAssigned);
  clearInterval(pollTimer);
});
</script>

<template>
  <div>
    <button
      ref="button"
      v-tooltip.right="LABELS.tooltip"
      class="size-8 rounded-lg hover:bg-n-alpha-1 flex-shrink-0 grid place-content-center relative border-0 bg-transparent cursor-pointer"
      :class="{ 'bg-n-alpha-2': isOpen }"
      @click="toggle"
    >
      <span class="i-lucide-list-checks size-4 text-n-slate-11" />
      <span
        v-if="badge"
        class="min-h-2 min-w-2 p-0.5 px-1 bg-n-ruby-9 rounded-lg absolute -top-1 -right-1.5 grid place-items-center text-[9px] leading-none text-n-ruby-3"
      >
        {{ badge }}
      </span>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        v-on-click-outside="closePanel"
        class="fixed z-[60] w-[360px] max-h-[70vh] flex flex-col bg-n-solid-1 border border-n-weak rounded-xl shadow-lg overflow-hidden"
        :style="panelStyle"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
        >
          <h3 class="text-sm font-semibold text-n-slate-12 m-0">
            {{ LABELS.title }}
          </h3>
          <button
            class="text-xs text-n-brand hover:underline border-0 bg-transparent cursor-pointer px-0"
            @click="
              store.close();
              router.push({
                name: 'kanban_tasks',
                params: { accountId: route.params.accountId },
              });
            "
          >
            {{ LABELS.seeAll }}
          </button>
        </div>

        <div class="flex-1 overflow-y-auto">
          <p
            v-if="isLoading && !sections.length"
            class="text-xs text-n-slate-11 px-4 py-6 text-center m-0"
          >
            {{ LABELS.loading }}
          </p>
          <p
            v-else-if="!sections.length"
            class="text-sm text-n-slate-11 px-4 py-6 text-center m-0"
          >
            {{ LABELS.empty }}
          </p>

          <div v-for="section in sections" :key="section.key" class="py-1">
            <p
              class="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide m-0"
              :class="{
                'text-n-brand': section.key === 'new',
                'text-n-ruby-11': section.key === 'overdue',
                'text-n-amber-11': section.key === 'today',
                'text-n-slate-11': section.key === 'upcoming',
              }"
            >
              {{ section.label }} · {{ section.tasks.length }}
            </p>
            <div
              v-for="task in section.tasks"
              :key="task.id"
              class="flex items-start gap-2 px-4 py-2 hover:bg-n-alpha-1 cursor-pointer"
              @click="openTask(task)"
            >
              <input
                v-tooltip.top="LABELS.done"
                type="checkbox"
                class="mt-1 cursor-pointer"
                @click.stop="complete(task)"
              />
              <div class="min-w-0 flex-1">
                <p class="text-sm text-n-slate-12 m-0 truncate">
                  {{ ACTIVITY_EMOJI[task.activity_type] || '📋' }}
                  {{ task.title }}
                </p>
                <p class="text-xs text-n-slate-11 m-0 truncate">
                  {{ contextLabel(task) }} · {{ dueLabel(task) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
